import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar as CalendarIcon,
  CheckCircle,
  Clock,
  AlertCircle,
  DollarSign,
  TrendingUp,
  RefreshCw,
  Banknote,
  Building2,
  ArrowDownLeft,
  ArrowUpRight,
  Wallet,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { apiClient, BACKEND_URL } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';

const API = `${BACKEND_URL}/api`;

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const ClosingsCalendar = ({ user }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showDayDialog, setShowDayDialog] = useState(false);
  const [dayDetails, setDayDetails] = useState(null);
  const [loadingDayDetails, setLoadingDayDetails] = useState(false);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth() + 1; // 1-indexed

  const fetchCalendarData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`${API}/dayclosings/calendar/${year}/${month}`);
      setCalendarData(response.data);
    } catch (error) {
      console.error('Failed to fetch calendar data:', error);
      toast.error('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  const fetchDayDetails = async (dateStr) => {
    setLoadingDayDetails(true);
    try {
      const response = await apiClient.get(`${API}/dayclosings/day-details/${dateStr}`);
      setDayDetails(response.data);
    } catch (error) {
      console.error('Failed to fetch day details:', error);
      toast.error('Failed to load day details');
    } finally {
      setLoadingDayDetails(false);
    }
  };

  useEffect(() => {
    fetchCalendarData();
  }, [fetchCalendarData]);

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(year, month - 2, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(year, month, 1));
  };

  const goToCurrentMonth = () => {
    setCurrentDate(new Date());
  };

  // Generate calendar grid
  const generateCalendarDays = () => {
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Empty cells for days before the first of the month
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push({ day: null, isEmpty: true });
    }
    
    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const closings = calendarData?.by_date?.[dateStr] || [];
      const today = new Date();
      const isToday = today.getFullYear() === year && 
                      today.getMonth() + 1 === month && 
                      today.getDate() === day;
      const isFuture = new Date(dateStr) > today;
      
      days.push({
        day,
        dateStr,
        closings,
        hasClosing: closings.length > 0,
        isToday,
        isFuture,
        isEmpty: false
      });
    }
    
    return days;
  };

  const handleDayClick = (dayData) => {
    if (dayData.isEmpty || dayData.isFuture) return;
    setSelectedDay(dayData);
    setDayDetails(null);
    setShowDayDialog(true);
    fetchDayDetails(dayData.dateStr);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-500';
      case 'closed': return 'bg-yellow-500';
      case 'draft': return 'bg-gray-400';
      default: return 'bg-gray-300';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'closed': return <Clock className="w-4 h-4 text-yellow-600" />;
      default: return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const calendarDays = calendarData ? generateCalendarDays() : [];
  const isCurrentMonth = new Date().getFullYear() === year && new Date().getMonth() + 1 === month;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <CalendarIcon className="w-6 h-6 text-blue-600" />
            Day Closings Calendar
          </h2>
          <p className="text-gray-500 text-sm mt-1">Monthly overview of all day closings</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchCalendarData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {!isCurrentMonth && (
            <Button variant="outline" size="sm" onClick={goToCurrentMonth}>
              Today
            </Button>
          )}
        </div>
      </div>

      {/* Month Summary Cards */}
      {calendarData?.totals && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-xs">Days with Closings</p>
                  <p className="text-2xl font-bold">{calendarData.totals.days_with_closings}</p>
                </div>
                <CalendarIcon className="w-8 h-8 text-blue-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-xs">Gross Sales</p>
                  <p className="text-2xl font-bold">{formatCurrency(calendarData.totals.gross_sales)}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-green-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-100 text-xs">Total Deductions</p>
                  <p className="text-2xl font-bold">{formatCurrency(calendarData.totals.total_deductions)}</p>
                </div>
                <DollarSign className="w-8 h-8 text-orange-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-xs">Net Amount</p>
                  <p className="text-2xl font-bold">{formatCurrency(calendarData.totals.net_amount)}</p>
                </div>
                <DollarSign className="w-8 h-8 text-purple-200" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Calendar */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={goToPreviousMonth}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <CardTitle className="text-xl">
              {MONTHS[month - 1]} {year}
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={goToNextMonth}>
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {/* Day headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {DAYS_OF_WEEK.map(day => (
                  <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Calendar grid */}
              <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((dayData, index) => (
                  <div
                    key={index}
                    onClick={() => handleDayClick(dayData)}
                    className={`
                      min-h-[80px] p-1 rounded-lg border transition-all
                      ${dayData.isEmpty ? 'bg-gray-50 border-transparent' : 'border-gray-200'}
                      ${dayData.isToday ? 'ring-2 ring-blue-500 bg-blue-50' : ''}
                      ${dayData.isFuture ? 'bg-gray-50 text-gray-400' : ''}
                      ${!dayData.isEmpty && !dayData.isFuture ? 'cursor-pointer hover:bg-gray-50 hover:shadow-md' : ''}
                      ${dayData.hasClosing ? 'bg-green-50' : ''}
                    `}
                  >
                    {!dayData.isEmpty && (
                      <>
                        <div className={`text-sm font-medium ${dayData.isToday ? 'text-blue-600' : ''}`}>
                          {dayData.day}
                        </div>
                        {dayData.hasClosing && (
                          <div className="mt-1 space-y-1">
                            {dayData.closings.slice(0, 2).map((closing, i) => (
                              <div 
                                key={closing.id}
                                className="flex items-center gap-1 text-xs"
                              >
                                <div className={`w-2 h-2 rounded-full ${getStatusColor(closing.status)}`}></div>
                                <span className="truncate font-medium">
                                  {formatCurrency(closing.total_sales)}
                                </span>
                              </div>
                            ))}
                            {dayData.closings.length > 2 && (
                              <div className="text-xs text-gray-500">
                                +{dayData.closings.length - 2} more
                              </div>
                            )}
                          </div>
                        )}
                        {!dayData.hasClosing && !dayData.isFuture && (
                          <div className="mt-2 text-xs text-red-400 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            Missing
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Legend */}
              <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t">
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span>Approved</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <span>Closed</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-3 h-3 rounded-full bg-gray-400"></div>
                  <span>Draft</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-red-500">
                  <AlertCircle className="w-3 h-3" />
                  <span>Missing</span>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Day Details Dialog */}
      <Dialog open={showDayDialog} onOpenChange={setShowDayDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-blue-600" />
              {selectedDay?.dateStr}
            </DialogTitle>
          </DialogHeader>
          
          {loadingDayDetails ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : dayDetails?.has_closing ? (
            <div className="space-y-6">
              {/* Cashier Section */}
              <div>
                <h3 className="text-lg font-semibold flex items-center gap-2 mb-3">
                  <Wallet className="w-5 h-5 text-green-600" />
                  Cashier Summary
                </h3>
                <div className="bg-green-50 rounded-lg p-4 space-y-3">
                  {/* Status Row */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(dayDetails.cashier_summary?.status)}
                      <Badge variant={dayDetails.cashier_summary?.status === 'approved' ? 'default' : 'secondary'}>
                        {dayDetails.cashier_summary?.status?.toUpperCase()}
                      </Badge>
                    </div>
                    <span className="text-sm text-gray-500">{dayDetails.cashier_summary?.cashier_name}</span>
                  </div>
                  
                  <Separator />
                  
                  {/* Cashier Details Grid */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-xs text-gray-500">Opening Cash</p>
                      <p className="text-lg font-semibold">{formatCurrency(dayDetails.cashier_summary?.opening_cash)}</p>
                    </div>
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <ArrowDownLeft className="w-3 h-3 text-blue-500" />
                        Cash Received from Bank
                      </p>
                      <p className="text-lg font-semibold text-blue-600">{formatCurrency(dayDetails.cashier_summary?.cash_received)}</p>
                    </div>
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-xs text-gray-500">Total Sales</p>
                      <p className="text-lg font-semibold">{formatCurrency(dayDetails.cashier_summary?.total_sales)}</p>
                    </div>
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-xs text-gray-500">Deductions</p>
                      <p className="text-lg font-semibold text-red-600">-{formatCurrency(dayDetails.cashier_summary?.total_deductions)}</p>
                    </div>
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-xs text-gray-500">Net Amount</p>
                      <p className="text-lg font-semibold text-green-600">{formatCurrency(dayDetails.cashier_summary?.net_amount)}</p>
                    </div>
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-xs text-gray-500">Closing Cash</p>
                      <p className="text-lg font-semibold text-purple-600">{formatCurrency(dayDetails.cashier_summary?.closing_cash)}</p>
                    </div>
                  </div>
                  
                  {/* Bank Deposit from Cashier */}
                  <div className="bg-blue-100 rounded-lg p-3">
                    <p className="text-xs text-blue-700 flex items-center gap-1">
                      <ArrowUpRight className="w-3 h-3" />
                      Bank Deposit (Online Payments + Bank Deposits)
                    </p>
                    <p className="text-xl font-bold text-blue-700">{formatCurrency(dayDetails.cashier_summary?.bank_deposit)}</p>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Bank Section */}
              <div>
                <h3 className="text-lg font-semibold flex items-center gap-2 mb-3">
                  <Building2 className="w-5 h-5 text-blue-600" />
                  Bank Summary
                </h3>
                <div className="bg-blue-50 rounded-lg p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-xs text-gray-500">Bank Opening Total</p>
                      <p className="text-lg font-semibold">{formatCurrency(dayDetails.bank_summary?.opening_total)}</p>
                    </div>
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <ArrowDownLeft className="w-3 h-3 text-green-500" />
                        Deposits Today
                      </p>
                      <p className="text-lg font-semibold text-green-600">+{formatCurrency(dayDetails.bank_summary?.deposits_today)}</p>
                    </div>
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <ArrowUpRight className="w-3 h-3 text-purple-500" />
                        Withdrawal to Cashier
                      </p>
                      <p className="text-lg font-semibold text-purple-600">-{formatCurrency(dayDetails.bank_summary?.withdrawal_to_cashier)}</p>
                    </div>
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <ArrowUpRight className="w-3 h-3 text-red-500" />
                        Bank Expenses
                      </p>
                      <p className="text-lg font-semibold text-red-600">-{formatCurrency(dayDetails.bank_summary?.expenses)}</p>
                    </div>
                  </div>
                  
                  {/* Bank Closing */}
                  <div className="bg-blue-100 rounded-lg p-3">
                    <p className="text-xs text-blue-700">Bank Closing Total</p>
                    <p className="text-xl font-bold text-blue-700">{formatCurrency(dayDetails.bank_summary?.closing_total)}</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
              <p className="text-gray-600 font-medium text-lg">No closing for this day</p>
              <p className="text-gray-400 text-sm mt-2">Day closing was not recorded</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClosingsCalendar;
